import * as React from 'react'
import { Navigate, RouteObject } from 'react-router'
import { Authenticate } from '../../auth/AuthenticateRoutes'
import { NotFound } from '../../routes/NotFound'
import { OrgOnboarding } from './OnboardingLayout'
import { OnboardingStepWrapper } from './OnboardingStepWrapper'
import { STEP_ROUTE_MAPPING, getAllStepConfigs } from './stepConfiguration'
import { useOnboardingNavigation } from './useOnboardingNavigation'

// Components are now loaded from step configuration modules

/**
 * Default onboarding route that redirects to the first incomplete step
 */
const OnboardingDefaultRoute = () => {
  const { getFirstIncompleteStep } = useOnboardingNavigation()
  const firstIncompleteStep = getFirstIncompleteStep()
  const routePath = STEP_ROUTE_MAPPING[firstIncompleteStep]

  return <Navigate to={routePath} replace />
}

/**
 * Generate ALL individual step routes - some orgs will not have to complete certain steps,
 * but we want to make sure they can access the steps that are relevant to them
 */
const generateStepRoutes = (): RouteObject[] => {
  const stepConfigs = getAllStepConfigs()

  return stepConfigs.map(({ routePath, Component, stepType, stepTitle }) => ({
    path: routePath,
    element: (
      <OnboardingStepWrapper stepType={stepType}>
        <React.Suspense fallback={<div>Loading...</div>}>
          <Component />
        </React.Suspense>
      </OnboardingStepWrapper>
    ),
    handle: { title: stepTitle },
  }))
}

/**
 * Onboarding routes configuration
 * These routes will be nested under /manage/org/:orgId/onboard/
 */
export const onboardingRoutes: RouteObject[] = [
  {
    path: 'org/:orgId/onboard',
    element: (
      <Authenticate requiredProfile="organization_admin" accessCheck="orgAdminDashboard">
        <OrgOnboarding />
      </Authenticate>
    ),
    handle: { title: 'Onboarding' },
    children: [
      // Default route - redirects to first incomplete step
      { index: true, element: <OnboardingDefaultRoute /> },

      // Individual step routes
      ...generateStepRoutes(),

      // Catch-all for invalid onboarding routes
      { path: '*', element: <NotFound /> },
    ],
  },
]
